/**
 * TabCoordinator — Prevents two browser tabs from running the engine simultaneously.
 *
 * Uses BroadcastChannel so tabs can detect each other without polling.
 * One tab is the "leader" (runs audio + engine). Others are observers.
 *
 * Lifecycle:
 *   1. On boot, call queryOtherTabs() before auto-resuming.
 *   2. If another tab responds as active → don't start engine here.
 *   3. When this tab's engine starts → call setActive(retreatId, name).
 *   4. When engine stops normally → call broadcastEnd() + setInactive().
 *   5. When another tab takes ownership → onOwnershipTransfer fires.
 *   6. When another tab ends the retreat → onRetreatEnded fires.
 */

import { createChildLogger } from "@retreat/shared";
import { TIMING } from "../constants";

const log = createChildLogger("TabCoordinator");

type TabMsg =
  | { type: "PING" }
  | { type: "PONG"; active: boolean; retreatId: string | null; retreatName: string | null }
  | { type: "OWNERSHIP_TRANSFER"; retreatId: string; retreatName: string | null }
  | { type: "END_RETREAT" };

export type OtherTabInfo = { retreatId: string; retreatName: string | null };

const CHANNEL_NAME = "retreat-os-tabs";

class TabCoordinator {
  private static _instance: TabCoordinator | null = null;

  private channel: BroadcastChannel;
  private _activeRetreatId: string | null = null;
  private _activeRetreatName: string | null = null;
  private _onOwnershipTransfer: ((retreatName: string | null) => void) | null = null;
  private _onRetreatEnded: (() => void) | null = null;

  private constructor() {
    this.channel = new BroadcastChannel(CHANNEL_NAME);
    this.channel.onmessage = (e: MessageEvent<TabMsg>) => {
      if (e.data.type === "PING") {
        log.flow("PING received — responding", { active: this._activeRetreatId !== null, retreatId: this._activeRetreatId });
        this.channel.postMessage({
          type: "PONG",
          active: this._activeRetreatId !== null,
          retreatId: this._activeRetreatId,
          retreatName: this._activeRetreatName,
        } satisfies TabMsg);
      } else if (e.data.type === "OWNERSHIP_TRANSFER") {
        log.info("OWNERSHIP_TRANSFER received", { retreatName: e.data.retreatName });
        this._activeRetreatId = null;
        this._activeRetreatName = null;
        this._onOwnershipTransfer?.(e.data.retreatName);
      } else if (e.data.type === "END_RETREAT") {
        log.info("END_RETREAT received");
        this._onRetreatEnded?.();
      }
    };
  }

  static get(): TabCoordinator {
    if (!TabCoordinator._instance) {
      TabCoordinator._instance = new TabCoordinator();
    }
    return TabCoordinator._instance;
  }

  /**
   * Ask other tabs if any are actively running a retreat.
   * Returns { retreatId, retreatName } if another tab is active, null otherwise.
   */
  queryOtherTabs(): Promise<OtherTabInfo | null> {
    log.flow("queryOtherTabs: broadcasting PING", { timeoutMs: TIMING.TAB_QUERY_TIMEOUT_MS });
    return new Promise((resolve) => {
      let found: OtherTabInfo | null = null;

      const handler = (e: MessageEvent<TabMsg>) => {
        if (e.data.type === "PONG" && e.data.active && e.data.retreatId) {
          found = { retreatId: e.data.retreatId, retreatName: e.data.retreatName };
          log.info("queryOtherTabs: active tab detected", { retreatId: found.retreatId });
        }
      };

      this.channel.addEventListener("message", handler);
      this.channel.postMessage({ type: "PING" } satisfies TabMsg);

      setTimeout(() => {
        this.channel.removeEventListener("message", handler);
        if (!found) log.flow("queryOtherTabs: no active tab found");
        resolve(found);
      }, TIMING.TAB_QUERY_TIMEOUT_MS);
    });
  }

  /** Register a callback fired when another tab takes ownership of the retreat. */
  onOwnershipTransfer(cb: (retreatName: string | null) => void): void {
    this._onOwnershipTransfer = cb;
  }

  /** Register a callback fired when another tab broadcasts retreat end. */
  onRetreatEnded(cb: () => void): void {
    this._onRetreatEnded = cb;
  }

  /** Broadcast that this tab is taking ownership; other tabs should stop their engines. */
  transferOwnership(retreatId: string, retreatName: string | null): void {
    log.info("transferOwnership: broadcasting", { retreatId });
    this.channel.postMessage({ type: "OWNERSHIP_TRANSFER", retreatId, retreatName } satisfies TabMsg);
  }

  /** Broadcast that the retreat has ended (user stopped it); other tabs clear their cards. */
  broadcastEnd(): void {
    log.info("broadcastEnd: broadcasting END_RETREAT");
    this.channel.postMessage({ type: "END_RETREAT" } satisfies TabMsg);
  }

  setActive(retreatId: string, retreatName?: string | null): void {
    log.info("setActive: this tab now owns retreat", { retreatId });
    this._activeRetreatId = retreatId;
    this._activeRetreatName = retreatName ?? null;
  }

  setInactive(): void {
    log.info("setInactive: retreat released", { retreatId: this._activeRetreatId });
    this._activeRetreatId = null;
    this._activeRetreatName = null;
  }

  get isActive(): boolean {
    return this._activeRetreatId !== null;
  }
}

export { TabCoordinator };
